import { STORE_KEY, makeEmptyStore, migrateStore, validateTedStore, type TedStore } from "./ted-model";

const DATABASE_NAME = "ted-track-every-dose";
const DATABASE_VERSION = 1;
const STATE_STORE = "app-state";
const CURRENT_STATE_KEY = "current";
const LOCAL_FALLBACK_FLAG = "ted_storage_fallback";
const LEGACY_MIGRATION_MARKER = "ted_indexeddb_migrated_at";

/** This device cannot provide IndexedDB at all, so nothing has been written there yet. */
export class TedStorageUnavailableError extends Error {
  constructor(options?: { cause?: unknown }) {
    super("TED storage could not be opened on this device", options);
    this.name = "TedStorageUnavailableError";
  }
}

/**
 * Storage opened, but a record already saved on this device could not be read or migrated.
 * The medicines and history are still there. Never fall back to another adapter and never
 * substitute an empty store: both present as data loss and invite the user to re-enter
 * records they still have.
 */
export class TedStorageUnreadableError extends Error {
  constructor(options?: { cause?: unknown }) {
    super("TED could not read the data already saved on this device", options);
    this.name = "TedStorageUnreadableError";
  }
}

export interface TedRepository {
  load(): Promise<TedStore | null>;
  save(store: TedStore): Promise<void>;
}

type StateRecord = { key: typeof CURRENT_STATE_KEY; value: TedStore };

const requestResult = <T,>(request: IDBRequest<T>) => new Promise<T>((resolve, reject) => {
  request.addEventListener("success", () => resolve(request.result), { once: true });
  request.addEventListener("error", () => reject(request.error ?? new Error("IndexedDB request failed")), { once: true });
});

const transactionComplete = (transaction: IDBTransaction) => new Promise<void>((resolve, reject) => {
  transaction.addEventListener("complete", () => resolve(), { once: true });
  transaction.addEventListener("abort", () => reject(transaction.error ?? new Error("IndexedDB transaction aborted")), { once: true });
  transaction.addEventListener("error", () => reject(transaction.error ?? new Error("IndexedDB transaction failed")), { once: true });
});

let databasePromise: Promise<IDBDatabase> | undefined;

function openDatabase() {
  if (!databasePromise) {
    databasePromise = new Promise<IDBDatabase>((resolve, reject) => {
      if (typeof indexedDB === "undefined") {
        reject(new TedStorageUnavailableError());
        return;
      }
      let request: IDBOpenDBRequest;
      try {
        request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
      } catch (cause) {
        reject(new TedStorageUnavailableError({ cause }));
        return;
      }
      request.addEventListener("upgradeneeded", () => {
        if (!request.result.objectStoreNames.contains(STATE_STORE)) {
          request.result.createObjectStore(STATE_STORE, { keyPath: "key" });
        }
      });
      request.addEventListener("success", () => resolve(request.result), { once: true });
      request.addEventListener("error", () => reject(new TedStorageUnavailableError({ cause: request.error })), { once: true });
      request.addEventListener("blocked", () => reject(new TedStorageUnavailableError({ cause: request.error })), { once: true });
    });
    // A failed open must not be cached, so a later launch can try again.
    databasePromise.catch(() => { databasePromise = undefined; });
  }
  return databasePromise;
}

export class IndexedDbTedRepository implements TedRepository {
  async load() {
    const database = await openDatabase();
    let record: StateRecord | undefined;
    try {
      const transaction = database.transaction(STATE_STORE, "readonly");
      record = await requestResult(transaction.objectStore(STATE_STORE).get(CURRENT_STATE_KEY)) as StateRecord | undefined;
      await transactionComplete(transaction);
    } catch (cause) {
      throw new TedStorageUnreadableError({ cause });
    }
    if (!record) return null;
    try {
      return migrateStore(record.value);
    } catch (cause) {
      throw new TedStorageUnreadableError({ cause });
    }
  }

  async save(store: TedStore) {
    const database = await openDatabase();
    const transaction = database.transaction(STATE_STORE, "readwrite");
    transaction.objectStore(STATE_STORE).put({ key: CURRENT_STATE_KEY, value: validateTedStore(store) } satisfies StateRecord);
    await transactionComplete(transaction);
  }
}

export class LocalStorageTedRepository implements TedRepository {
  async load() {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return null;
    try {
      return migrateStore(JSON.parse(raw));
    } catch (cause) {
      throw new TedStorageUnreadableError({ cause });
    }
  }

  async save(store: TedStore) {
    localStorage.setItem(STORE_KEY, JSON.stringify(validateTedStore(store)));
  }
}

let repositoryPromise: Promise<TedRepository> | undefined;
let saveQueue: Promise<void> = Promise.resolve();

async function selectRepository(): Promise<TedRepository> {
  if (localStorage.getItem(LOCAL_FALLBACK_FLAG) === "1") {
    const local = new LocalStorageTedRepository();
    // Anything recorded since the fallback began lives in localStorage. Switching back
    // to IndexedDB would hide it, so only reconsider when the fallback holds nothing.
    if (localStorage.getItem(STORE_KEY) !== null) return local;
    const indexedDb = new IndexedDbTedRepository();
    try {
      await indexedDb.load();
      localStorage.removeItem(LOCAL_FALLBACK_FLAG);
      return indexedDb;
    } catch (error) {
      if (error instanceof TedStorageUnreadableError) throw error;
      return local;
    }
  }

  const indexedDb = new IndexedDbTedRepository();
  try {
    await indexedDb.load();
    return indexedDb;
  } catch (error) {
    // Data that exists but cannot be read must surface as an error. Falling back here
    // would strand it and start writing a second, separate store alongside it.
    if (error instanceof TedStorageUnreadableError) throw error;
    localStorage.setItem(LOCAL_FALLBACK_FLAG, "1");
    return new LocalStorageTedRepository();
  }
}

function repository() {
  repositoryPromise ??= selectRepository().catch((error) => {
    repositoryPromise = undefined;
    throw error;
  });
  return repositoryPromise;
}

export async function loadTedStore() {
  const selected = await repository();
  const existing = await selected.load();
  if (existing) return existing;

  const legacyRaw = localStorage.getItem(STORE_KEY);
  if (legacyRaw) {
    let migrated: TedStore;
    try {
      migrated = migrateStore(JSON.parse(legacyRaw));
    } catch (cause) {
      throw new TedStorageUnreadableError({ cause });
    }
    await selected.save(migrated);
    localStorage.setItem(LEGACY_MIGRATION_MARKER, new Date().toISOString());
    return migrated;
  }

  // Reached only when storage opened cleanly and genuinely holds nothing.
  const empty = makeEmptyStore();
  await selected.save(empty);
  return empty;
}

export async function saveTedStore(store: TedStore) {
  const snapshot = validateTedStore(store);
  const write = saveQueue.then(async () => (await repository()).save(snapshot));
  saveQueue = write.catch(() => undefined);
  return write;
}

/** Which adapter is in use, in words, for the support summary. */
export function describeStorage() {
  try {
    if (localStorage.getItem(LOCAL_FALLBACK_FLAG) === "1") return "browser storage (fallback)";
  } catch {
    return "unknown";
  }
  return typeof indexedDB === "undefined" ? "unavailable" : "IndexedDB";
}

export async function requestPersistentStorage() {
  if (!navigator.storage?.persisted || !navigator.storage.persist) return false;
  if (await navigator.storage.persisted()) return true;
  return navigator.storage.persist();
}

export async function resetTedStorageForTests() {
  await saveQueue.catch(() => undefined);
  saveQueue = Promise.resolve();
  if (databasePromise) {
    try { (await databasePromise).close(); } catch { /* database never opened */ }
  }
  databasePromise = undefined;
  repositoryPromise = undefined;
  await new Promise<void>((resolve, reject) => {
    const request = indexedDB.deleteDatabase(DATABASE_NAME);
    request.addEventListener("success", () => resolve(), { once: true });
    request.addEventListener("error", () => reject(request.error ?? new Error("Could not reset test database")), { once: true });
    request.addEventListener("blocked", () => resolve(), { once: true });
  });
}
